import { useParams, useNavigate } from 'react-router-dom';
import { useBudget, useBudgets, useUpdateBudget } from '@/hooks/useBudgets';
import { BudgetForm } from '@/components/budget/BudgetForm';
import { LoadingOverlay } from '@/components/ui/Loading';
import { ApiException } from '@/lib/api';
import { BudgetService } from '@/services/BudgetService';
import { toast } from 'sonner';
import type { Budget, ConflictError, UpdateBudgetRequest, CreateBudgetRequest } from '@/types';

interface BudgetConflictNavigationState {
  serverBudget: Budget;
  clientRequest: UpdateBudgetRequest;
  message: string;
}

function conflictStorageKey(budgetId: string): string {
  return `budget-conflict:${budgetId}`;
}

export function EditBudgetPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: budget, isLoading } = useBudget(id);
  const { data: budgets = [] } = useBudgets();
  const updateBudget = useUpdateBudget();

  if (isLoading) {
    return <LoadingOverlay message="Loading budget..." />;
  }

  if (!budget) {
    navigate('/budgets');
    return null;
  }

  const handleSubmit = async (data: CreateBudgetRequest | UpdateBudgetRequest) => {
    const updateRequest = data as UpdateBudgetRequest;

    try {
      await updateBudget.mutateAsync({ id: budget.id, request: updateRequest });
      navigate(`/budgets/${budget.id}`);
    } catch (error) {
      if (error instanceof ApiException && error.isConflict) {
        const conflictError = error.error as Partial<ConflictError>;
        const latest = await BudgetService.getById(budget.id);

        if (!latest) {
          toast.error('Unable to load latest budget after conflict. Please refresh and try again.');
          return;
        }

        const conflictState: BudgetConflictNavigationState = {
          serverBudget: latest,
          clientRequest: updateRequest,
          message:
            conflictError.message ??
            'Budget was modified by another user. Choose which version to keep.',
        };

        sessionStorage.setItem(conflictStorageKey(budget.id), JSON.stringify(conflictState));
        navigate(`/budgets/${budget.id}/edit/conflict`, { state: conflictState });
        return;
      }

      throw error;
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Flatten budgets (including children) for parent selection (excluding self)
  const flattenBudgets = (items: any[]): any[] => {
    const result: any[] = [];
    const seen = new Set<string>();
    
    const addBudget = (item: any) => {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        result.push(item);
      }
      if (item.children?.length) {
        item.children.forEach(addBudget);
      }
    };
    
    items.forEach(addBudget);
    return result;
  };

  const parentBudgets = flattenBudgets(budgets).filter((b) => b.id !== budget.id);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Edit Budget</h1>
        <p className="text-sm text-muted-foreground">
          Update "{budget.name}"
        </p>
      </div>

      <BudgetForm
        budget={budget}
        parentBudgets={parentBudgets}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={updateBudget.isPending}
      />
    </div>
  );
}
